import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import queryString from 'query-string';
import { isNil } from 'lodash';

import {
  getAssignmentAdditionalInfo,
  getAssignmentAdminAdditionalInfo,
  getAssignmentPrimaryDetails,
  getAssignmentPrimaryDetailsByKey,
  getAssignmentTerminationModel,
  getAssignmentTypesPayCode,
  getPayCodesDetailsInfo,
  getPayRateTypes,
  getAssignmentPayCodeGroups,
} from 'redux/reducers/assignmentSlice';
import BaseAlert from 'components/wrappers/BaseAlert';
import { AssignmentDetailsModel } from 'utils/assignmentUtils';
import { AssignmentInfoTypes } from '../enums';
import AssignmentProgressBar from './AssignmentProgressBar';
import { AssignmentAdminDocumentModel } from 'api/adminApiClient';
import {
  AssignmentAdditionalInfoModel,
  AssignmentInfoUserResponseModel,
  AssignmentStatusTypeEnum,
  AssignmentTerminationModel,
} from 'api/backofficeApiClient';
import { isURLWithId } from 'utils/urlUtils';
import useQueryMatch from 'hooks/useQueryMatch';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import ActivitiesDetailsSection from 'components/ActivitiesDetailsSection';
import AssignmentsPrimaryInfoSection from './AssignmentPrimaryInfoSection';
import AssignmentAdditionalInfoSection from './AssignmentAdditionalInfoSection';
import { ActivitiesSectionPageTypeEnum } from 'components/ActivitiesDetailsSection/enums';
import AdminAssignmentAdditionalInfoSection from 'features/admin/assignment/AdminAssignmentAdditionalInfoSection';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './AssignmentDetailsPage.module.less';

type ParamsProps = {
  assignmentKey: string;
};

export enum AssignmentDetailsEnum {
  PrimaryInformation = 1,
  AdditionalInformation = 2,
}

const DEFAULT_SECTION = AssignmentInfoTypes.Primary;

const getPageSections = (
  isAdditionalInfoExists: boolean,
  isAdmin?: boolean,
) => {
  if (isAdmin || isAdditionalInfoExists) {
    return Object.values(AssignmentInfoTypes);
  }

  return [DEFAULT_SECTION];
};

export default function AssignmentDetailsPage() {
  const { assignmentKey } = useParams<ParamsProps>();
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);
  const [sectionsStatuses, setSectionStatuses] = useState({
    [AssignmentDetailsEnum.PrimaryInformation]: false,
    [AssignmentDetailsEnum.AdditionalInformation]: false,
  });
  const isInitialLoaded = Object.values(sectionsStatuses).some(Boolean);
  const [primaryDetails, setPrimaryDetails] =
    useState<AssignmentDetailsModel>();
  const [assignmentTerminationDetails, setAssignmentTerminationDetails] =
    useState<AssignmentTerminationModel>();
  const [
    isAssignmentTerminationDetailsInitialLoaded,
    setIsAssignmentTerminationDetailsInitialLoaded,
  ] = useState(false);

  const [additionalDetails, setAdditionalDetails] =
    useState<AssignmentAdminDocumentModel>();
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const query = queryString.parse(location.search);
  const pageSectionMatch = useQueryMatch(
    getPageSections(!isNil(additionalDetails), isAdminRole),
  );
  const isId: boolean = isURLWithId(assignmentKey || '');
  useEffect(() => {
    if (!pageSectionMatch) {
      navigate(`${location.pathname}?section=${DEFAULT_SECTION}`, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSectionMatch, isNil(additionalDetails)]);

  useEffect(() => {
    dispatch(getPayRateTypes(isAdminRole));
    dispatch(getPayCodesDetailsInfo(isAdminRole));
    dispatch(getAssignmentPayCodeGroups(isAdminRole));
    dispatch(getAssignmentTypesPayCode(isAdminRole));
  }, [dispatch, isAdminRole]);

  const loadAdditionalDetails = useCallback(
    async (assignmentId: string) => {
      if (!assignmentId) return;

      const correctAction = isAdminRole
        ? getAssignmentAdminAdditionalInfo
        : getAssignmentAdditionalInfo;

      const result = await dispatch(correctAction(assignmentId));

      if (correctAction.fulfilled.match(result)) {
        setAdditionalDetails(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }
      handleSectionLoading(AssignmentDetailsEnum.AdditionalInformation, true);
    },
    [dispatch, isAdminRole],
  );

  const loadAssignmentTermination = useCallback(async () => {
    if (!primaryDetails?.assignmentId) return;
    const result = await dispatch(
      getAssignmentTerminationModel({
        assignmentId: primaryDetails.assignmentId,
        isAdmin: isAdminRole,
      }),
    );

    if (getAssignmentTerminationModel.fulfilled.match(result)) {
      setAssignmentTerminationDetails(result.payload);
    } else {
      setAssignmentTerminationDetails(undefined);
    }
    setIsAssignmentTerminationDetailsInitialLoaded(true);
  }, [primaryDetails?.assignmentId, dispatch, isAdminRole]);

  const loadPrimaryDetails = useCallback(async () => {
    if (!assignmentKey) return;

    const correctAction = isId
      ? getAssignmentPrimaryDetails
      : getAssignmentPrimaryDetailsByKey;
    const correctData = isId
      ? { assignmentKey, isAdminRole }
      : { assignmentKey, isAdminRole };

    const result = await dispatch(correctAction(correctData));

    if (correctAction.fulfilled.match(result)) {
      const responseInfo = result.payload as AssignmentInfoUserResponseModel;

      (responseInfo?.isAdditionalInfoExists || isAdminRole) &&
        (await loadAdditionalDetails(result.payload.assignmentId as string));

      setPrimaryDetails(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    handleSectionLoading(AssignmentDetailsEnum.PrimaryInformation, true);
  }, [dispatch, isId, assignmentKey, isAdminRole, loadAdditionalDetails]);

  const loadPrimaryAndTerminationData = useCallback(async () => {
    await loadAssignmentTermination();
    await loadPrimaryDetails();
  }, [loadAssignmentTermination, loadPrimaryDetails]);

  const handleSectionLoading = (
    sectionType: AssignmentDetailsEnum,
    status: boolean,
  ) => {
    setSectionStatuses((statuses) => ({
      ...statuses,
      [sectionType]: status,
    }));
  };

  useEffect(() => {
    loadAssignmentTermination();
    // eslint-disable-next-line
  }, [primaryDetails?.assignmentId]);

  useEffect(() => {
    loadPrimaryDetails();
  }, [loadPrimaryDetails]);

  useEffect(() => {
    if (!query.section) {
      navigate(`${location.pathname}?section=${AssignmentInfoTypes.Primary}`, {
        replace: true,
      });
    }
    // eslint-disable-next-line
  }, [query.section]);

  if (!isInitialLoaded) return <Spin size="large" />;

  if (!primaryDetails || (isAdminRole && !additionalDetails)) return null;

  const { assignmentStatus } = primaryDetails;

  const getSection = () => {
    switch (query.section) {
      case AssignmentInfoTypes.Additional:
        return isAdminRole ? (
          <AdminAssignmentAdditionalInfoSection
            assignmentPrimaryDetails={primaryDetails}
            assignmentAdditionalDetails={additionalDetails}
            onSubmit={() =>
              loadAdditionalDetails(primaryDetails?.assignmentId as string)
            }
            setPrimaryDetails={setPrimaryDetails}
          />
        ) : (
          <AssignmentAdditionalInfoSection
            assignmentPrimaryDetails={primaryDetails}
            assignmentAdditionalDetails={
              additionalDetails as AssignmentAdditionalInfoModel
            }
          />
        );

      case AssignmentInfoTypes.Primary:
      default:
        return (
          <AssignmentsPrimaryInfoSection
            assignmentDetails={primaryDetails}
            loadAssignmentDetails={loadPrimaryDetails}
            assignmentTerminationDetails={assignmentTerminationDetails}
            isAssignmentTerminationDetailsInitialLoaded={
              isAssignmentTerminationDetailsInitialLoaded
            }
            loadPrimaryAndTerminationData={loadPrimaryAndTerminationData}
          />
        );
    }
  };

  return (
    <div ref={containerRef} className="page-wrapper scroll-box">
      <div className="page-holder">
        {assignmentStatus !== AssignmentStatusTypeEnum.Draft && (
          <div className={styles.holder}>
            <AssignmentProgressBar
              assignment={primaryDetails}
              setPrimaryDetails={setPrimaryDetails}
              setAdditionalDetails={setAdditionalDetails}
            />
          </div>
        )}

        <>{getSection()}</>
        <div className="page-box page-inner">
          {isAdminRole && (
            <ActivitiesDetailsSection
              entityId={primaryDetails?.assignmentId as string}
              pageType={ActivitiesSectionPageTypeEnum.Assignment}
              scrollRef={containerRef}
            />
          )}
        </div>
      </div>
    </div>
  );
}
